import { useMemo, useState } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { bildirimOlayAdi } from "@koclukpanel/contracts";
import {
  Alan,
  BosDurum,
  Buton,
  FiltreKarti,
  Ikon,
  Iskelet,
  OnayKutusu,
  Rozet,
  Secim,
  Tablo,
  cn,
  Kart,
} from "@koclukpanel/ui";
import {
  ApiHatasi,
  bildirimHepsiOkundu,
  bildirimOkundu,
  bildirimSayaci,
  bildirimleriGetir,
  type Bildirim,
} from "./api";
import { bildirimHedefi } from "./bildirim-yonlendirme";
import { SayfaBasligiEki } from "./Kabuk";

/**
 * PANEL İÇİ BİLDİRİM — F-NTF-09
 *
 * ================================================================
 * BU EKRANIN VAR OLMA SEBEBİ
 * ================================================================
 * Şartnamenin kendi cümlesi: *"'Panel içi' bir kanal olarak
 * sayılıyorsa kullanıcının o bildirimleri göreceği bir yer vardır;
 * yoksa kanal veritabanına yazan ama kimseye ulaşmayan bir kayıttır."*
 *
 * 28.08.2026'da ölçüldü: `GET /bildirimler`, `Notification` modeli ve
 * okundu işaretleme çalışıyordu — hiçbir arayüz ucu çağırmıyordu.
 * Kullanıcı kendisine gönderilen hiçbir bildirimi göremiyordu.
 *
 * ================================================================
 * SÜZGEÇLERİN NEREDE ÇALIŞTIĞI
 * ================================================================
 * "Yalnızca okunmamış" SUNUCUDA — uç zaten destekliyor.
 *
 * Tarih aralığı ve olay türü İSTEMCİDE: getirilen küme zaten
 * kullanıcının kendi bildirimleri ve en fazla 100 satır. Sunucuya
 * süzgeç eklemek her süzgeç değişiminde ağ turu demek olurdu.
 *
 * ================================================================
 * ZİL SAYACI AYRI UÇTAN — LİSTEDEN SAYILMIYOR
 * ================================================================
 * Liste `take: 100` ile sınırlı. Zil önce listeyi çekip uzunluğuna
 * bakıyordu ve 02.09.2026'da canlı denemede iki hatası birden çıktı:
 * 566 okunmamış varken "100" yazıyordu (yanlış sayı) ve her 60
 * saniyede yalnızca saymak için 100 satır taşıyordu.
 *
 * `GET /bildirimler/sayac` ikisini de çözüyor. Bedeli: liste ile
 * sayaç AYRI sorgu anahtarları, ikisini birden tazelemek gerekiyor —
 * unutulursa "okudum ama zil düşmüyor" olur. `tazele` bunu tek yerde
 * yapıyor.
 */
/**
 * ================================================================
 * OLAY ADLARI SÖZLEŞME PAKETİNDE — BURADA DEĞİL
 * ================================================================
 * Sözlük 07.09.2026'ya kadar bu dosyada duruyordu. Aynı gelen kutusu
 * öğrenci uygulamasında da açılınca (F-NTF-09'un kendi cümlesi) sözlük
 * ikinci bir kopya kazanacaktı ve yeni bir olay eklendiğinde biri
 * güncellenip öteki geride kalırdı.
 *
 * Kod kullanıcıya GÖSTERİLMİYOR: `ProgramYayinlandi` bir kod, ekranda
 * okunacak şey değil. Tanınmayan kod ham hâliyle dönüyor — katalog
 * büyüdüğünde yeni olay süzgeçten DÜŞMESİN diye; düşseydi kullanıcı o
 * bildirimleri hiç süzemezdi. Gerekçe `bildirim-olayi.ts` içinde.
 */

function tarihSaat(iso: string): string {
  return new Date(iso).toLocaleString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * ZİL — kabuğun sağ üstünde, her ekranda aynı yerde.
 *
 * Okunmamış sayısını sunucudan alıyor ve gelen kutusuyla AYNI sorgu
 * anahtarını paylaşıyor: bir bildirim okundu işaretlendiğinde sayaç
 * kendiliğinden düşüyor. İki ayrı sorgu olsaydı zil ile liste
 * ayrışır ve "okudum ama sayaç düşmüyor" olurdu.
 */
export function BildirimZili({ onAc }: { onAc: () => void }) {
  const okunmamis = useQuery({
    queryKey: ["bildirim-sayac"],
    queryFn: bildirimSayaci,
    retry: false,
    /*
      Bildirim arka planda üretiliyor; ekran açıkken sayacın
      güncellenmesi için aralıklı tazeleme gerekiyor. 60 sn: bildirim
      "hemen görülmesi gereken" bir şey değil (o kanal e-posta/SMS),
      ama beş dakika beklemek de zil olmasını anlamsız kılardı.
    */
    refetchInterval: 60_000,
  });

  const sayi = okunmamis.data?.okunmamis ?? 0;

  return (
    <button
      type="button"
      onClick={onAc}
      /*
        UNİKOÇ zil düğmesi (sürüm 9a; tarama-1 §2.5): beyaz 44x44 kutu, ince açık kenar,
        12 px köşe, çizgi zil ikonu. Emoji zil her işletim sisteminde başka renkte çiziliyordu.
      */
      className="size-dokunma bg-notr-0 border-notr-200 text-metin-koyu hover:bg-notr-50 relative inline-flex items-center justify-center rounded-[0.75rem] border transition-colors duration-(--sure-hizli)"
    >
      {/*
        Ekran okuyucu için SAYI da duyuruluyor: yalnızca "Bildirimler"
        demek, görme engelli kullanıcıya okunmamış olup olmadığını
        söylemezdi — ve o bilgi zilin varlık sebebi.
      */}
      <span className="sr-only">
        {sayi === 0 ? "Bildirimler — okunmamış yok" : `Bildirimler — ${sayi} okunmamış`}
      </span>
      <Ikon ad="bildirim" className="size-5" />
      {sayi > 0 && (
        <span
          aria-hidden="true"
          /*
            Zemin `hata-700`, bir alt ton DEĞİL. İki sebep:

            1. Küçük bir rozette beyaz yazı için daha yüksek kontrast.
            2. Bir alt ton `packages/ui/src/tarama-kapsami.test.ts`in
               KANARYASI: o sınıfın YALNIZCA paylaşılan bileşenlerde
               geçmesi, `@source` satırının gerçekten iş gördüğünü
               kanıtlıyor. Uygulama kaynağında kullanmak — hatta
               yorumda ADINI YAZMAK bile — kanaryayı öldürüyor; test
               kaynak metnini tarıyor ve 02.09.2026'da tam olarak bu
               yüzden düştü.
          */
          className="rounded-rozet bg-hata-700 text-notr-0 absolute -top-0.5 -right-0.5 min-w-5 px-1 text-center text-xs font-medium tabular-nums"
        >
          {sayi > 99 ? "99+" : sayi}
        </span>
      )}
    </button>
  );
}

export function Bildirimler({ onGit }: { onGit?: (linkUrl: string) => void }) {
  const qc = useQueryClient();
  const [yalnizOkunmamis, setYalnizOkunmamis] = useState(false);
  // Tarih ARALIĞI (UNİKOÇ: Başlangıç / Bitiş); boş = sınırsız. Gün sınırı Europe/Istanbul (+03:00).
  const [baslangic, setBaslangic] = useState("");
  const [bitis, setBitis] = useState("");
  const [ara, setAra] = useState("");
  const [olay, setOlay] = useState("");

  /*
    ================================================================
    SAYFALAMA — 04.09.2026
    ================================================================
    Uç önce en yeni 100 kaydı döndürüyordu ve gerisine ulaşmanın
    yolu yoktu. Ölçek kararı (şartname §5.2: hedef 2.000 öğrenci /
    100 personel) bu sınırı aylar içinde vurur.

    `useInfiniteQuery` seçildi çünkü bildirim akışı SAYFA NUMARASI
    olan bir liste değil, aşağı doğru uzayan bir akış. Sayfa
    numarası gösterseydik, araya yeni bildirim düştüğünde "3. sayfa"
    dün gördüğün 3. sayfa olmazdı.
  */
  const liste = useInfiniteQuery({
    queryKey: ["bildirimler", yalnizOkunmamis],
    queryFn: ({ pageParam }) => bildirimleriGetir(yalnizOkunmamis, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (son) => son.sonrakiImlec ?? undefined,
    retry: false,
  });

  /*
    Sayfalar TEK LİSTEYE düzleştiriliyor. Süzgeçler (tarih, olay
    türü) bunun üstünde çalışıyor — yani YALNIZCA ÇEKİLMİŞ sayfaları
    süzüyorlar. Bu bir kusur ve bilerek böyle: sunucu tarafı süzgeç
    yazmak, süzgeç kombinasyonu başına ayrı bir sorgu demek. Ekran
    "daha eskisini yükle" düğmesini her zaman gösteriyor ki
    kullanıcı aradığını bulamadığında ne yapacağını bilsin.
  */
  const tumKayitlar = useMemo(
    () => (liste.data?.pages ?? []).flatMap((p) => p.bildirimler),
    [liste.data],
  );

  const tazele = async (): Promise<void> => {
    // LİSTE VE SAYAÇ AYRI ANAHTARLAR, ikisi de tazeleniyor.
    // Sayaç ayrı bir uçtan geliyor (liste `take: 100` ile sınırlı ve
    // saymaya uygun değil); tazelemeyi unutmak "okudum ama zil
    // düşmüyor" demek olurdu.
    await qc.invalidateQueries({ queryKey: ["bildirimler"] });
    await qc.invalidateQueries({ queryKey: ["bildirim-sayac"] });
  };

  const okundu = useMutation({
    mutationFn: (id: string) => bildirimOkundu(id),
    onSuccess: tazele,
  });

  const hepsi = useMutation({
    mutationFn: bildirimHepsiOkundu,
    onSuccess: tazele,
  });

  const olayTurleri = useMemo(() => {
    const kodlar = new Set(tumKayitlar.map((b) => b.eventCode));
    return [...kodlar].sort((a, b) => bildirimOlayAdi(a).localeCompare(bildirimOlayAdi(b), "tr"));
  }, [tumKayitlar]);

  const gorunen = useMemo(() => {
    const bas = baslangic === "" ? null : new Date(`${baslangic}T00:00:00+03:00`).getTime();
    const son = bitis === "" ? null : new Date(`${bitis}T23:59:59.999+03:00`).getTime();
    const aranan = ara.trim().toLocaleLowerCase("tr-TR");
    return tumKayitlar.filter((b) => {
      const an = new Date(b.createdAt).getTime();
      return (
        (olay === "" || b.eventCode === olay) &&
        (bas === null || an >= bas) &&
        (son === null || an <= son) &&
        (aranan === "" || `${b.title} ${b.body ?? ""}`.toLocaleLowerCase("tr-TR").includes(aranan))
      );
    });
  }, [tumKayitlar, baslangic, bitis, olay, ara]);

  const suzgecVar =
    baslangic !== "" || bitis !== "" || olay !== "" || yalnizOkunmamis || ara !== "";
  const suzgeciTemizle = (): void => {
    setBaslangic("");
    setBitis("");
    setOlay("");
    setAra("");
    setYalnizOkunmamis(false);
  };

  if (liste.isPending) return <Iskelet satir={5} />;

  if (liste.isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Bildirimler alınamadı"
        aciklama={liste.error instanceof ApiHatasi ? liste.error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void liste.refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  const okunmamisSayisi = tumKayitlar.filter((b) => !b.isRead).length;

  return (
    <div className="flex flex-col gap-6">
      {/*
        UNİKOÇ BİLDİRİMLERİM (sürüm 9a B2; tarama-2 §3.15): başlık bandında açıklama ve
        "Tümünü Okundu İşaretle" birincil düğmesi, altında filtre kartı (Başlangıç · Bitiş ·
        Tür · Sadece okunmamış), altında Saat · Tür · Mesaj · İşlemler tablosu. Eski kart
        yığını ve satır içi düğmeler kalktı; veri, süzgeç mantığı ve izinler aynı.
      */}
      <SayfaBasligiEki
        aciklama="Yönetici onay ve notlarını takip edin."
        eylemler={
          okunmamisSayisi > 0 ? (
            <Buton yukleniyor={hepsi.isPending} onClick={() => hepsi.mutate()}>
              Tümünü okundu işaretle
            </Buton>
          ) : undefined
        }
      />

      <FiltreKarti
        sutun={4}
        eylemler={
          <>
            <span className="text-metin-ikincil text-sm" aria-live="polite">
              {gorunen.length} / {tumKayitlar.length} bildirim
              {/*
                DAHA ESKİSİ VARSA SÖYLENİYOR. "50 / 50" yazıp susmak, kullanıcıya hepsinin bu
                olduğunu söylerdi.
              */}
              {liste.hasNextPage && <span> · daha eskisi var</span>}
            </span>
            {suzgecVar && (
              <Buton gorunum="kesikli" onClick={suzgeciTemizle}>
                Temizle
              </Buton>
            )}
          </>
        }
      >
        <Alan
          etiket="Başlangıç"
          type="date"
          value={baslangic}
          onChange={(e) => setBaslangic(e.target.value)}
        />
        <Alan etiket="Bitiş" type="date" value={bitis} onChange={(e) => setBitis(e.target.value)} />
        <Secim etiket="Tür" value={olay} onChange={(e) => setOlay(e.target.value)}>
          <option value="">Tümü</option>
          {olayTurleri.map((k) => (
            <option key={k} value={k}>
              {bildirimOlayAdi(k)}
            </option>
          ))}
        </Secim>
        {/*
          HAM `<input type="checkbox">` DEĞİL `OnayKutusu`: etiketi `htmlFor` ile bağlıyor,
          panelin odak halkasını taşıyor ve dokunma hedefi büyük. "Durum" başlığı UNİKOÇ'taki gibi
          kutunun üstünde.
        */}
        <div className="flex flex-col gap-1.5">
          <span className="text-metin-koyu text-sm font-semibold">Durum</span>
          <OnayKutusu
            etiket="Sadece okunmamış"
            checked={yalnizOkunmamis}
            onChange={(e) => setYalnizOkunmamis(e.target.checked)}
          />
        </div>
      </FiltreKarti>

      {(okundu.isError || hepsi.isError) && (
        <p role="alert" className="text-hata-500 text-sm font-medium">
          {(okundu.error ?? hepsi.error) instanceof ApiHatasi
            ? ((okundu.error ?? hepsi.error) as ApiHatasi).message
            : "İşlem tamamlanamadı."}
        </p>
      )}

      <Kart gorunum="unk" aria-label="Bildirim listesi" className="flex flex-col gap-4">
        <div className="flex justify-end">
          <div className="w-full sm:w-64">
            <Alan
              etiket="Ara"
              type="search"
              placeholder="Mesajda ara"
              value={ara}
              onChange={(e) => setAra(e.target.value)}
            />
          </div>
        </div>

        {gorunen.length === 0 ? (
          /*
            BOŞUN İKİ TÜRÜ. Süzgeç daralttıysa çözüm "süzgeci temizle"; gerçekten bildirim yoksa
            yapacak bir şey yok ve bunu söylemek gerekiyor — boş bir liste, bozuk bir liste gibi
            görünür.
          */
          tumKayitlar.length > 0 ? (
            <BosDurum
              ikon="bos"
              baslik="Bu süzgeçte bildirim yok"
              aciklama="Bildirim var ama süzgeç daralttı."
              eylem={
                <Buton gorunum="ikincil" onClick={suzgeciTemizle}>
                  Süzgeci temizle
                </Buton>
              }
            />
          ) : (
            <BosDurum
              ikon="bos"
              baslik={yalnizOkunmamis ? "Okunmamış bildiriminiz yok" : "Henüz bildiriminiz yok"}
              aciklama="Programınız yayınlandığında, raporunuz onaylandığında ya da hesabınıza yeni bir cihazdan girildiğinde burada görünür."
            />
          )
        ) : (
          <Tablo
            sutunlar={[
              {
                baslik: "Saat",
                mobil: true,
                className: "whitespace-nowrap",
                hucre: (b) => tarihSaat(b.createdAt),
              },
              { baslik: "Tür", mobil: true, hucre: (b) => bildirimOlayAdi(b.eventCode) },
              {
                baslik: "Mesaj",
                mobil: true,
                hucre: (b) => (
                  <span className="flex flex-col gap-0.5">
                    <span className={cn("text-metin-koyu", !b.isRead && "font-semibold")}>
                      {b.title}
                      {!b.isRead && (
                        <span className="ml-2 align-middle">
                          <Rozet ton="bilgi">Yeni</Rozet>
                        </span>
                      )}
                    </span>
                    {b.body && <span className="text-metin-ikincil">{b.body}</span>}
                  </span>
                ),
              },
              {
                baslik: "İşlemler",
                mobil: true,
                etiketGizli: true,
                hucre: (b) => (
                  <BildirimEylemleri
                    bildirim={b}
                    onOkundu={() => okundu.mutate(b.id)}
                    isleniyor={okundu.isPending && okundu.variables === b.id}
                    onGit={onGit}
                  />
                ),
              },
            ]}
            satirlar={gorunen}
            anahtar={(b) => b.id}
            kartBasligi={2}
          />
        )}

        {/*
          "DAHA ESKİSİNİ YÜKLE" — SÜZGEÇTEN BAĞIMSIZ. Süzgeçler yalnızca ÇEKİLMİŞ sayfaları süzüyor.
          Süzgeç sonucu boş çıkmış olsa bile düğme görünüyor: aradığı bildirim daha eski bir sayfada
          olabilir. UNİKOÇ'taki "Önceki / Sonraki" yerine tek düğme: akış sayfa numaralı bir liste
          değil (04.09.2026 gerekçesi yukarıda).
        */}
        {liste.hasNextPage && (
          <div className="flex justify-end">
            <Buton
              gorunum="ikincil"
              yukleniyor={liste.isFetchingNextPage}
              onClick={() => void liste.fetchNextPage()}
            >
              Daha eskisini yükle
            </Buton>
          </div>
        )}
      </Kart>
    </div>
  );
}

function BildirimEylemleri({
  bildirim,
  onOkundu,
  isleniyor,
  onGit,
}: {
  bildirim: Bildirim;
  onOkundu: () => void;
  isleniyor: boolean;
  onGit?: (linkUrl: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {/*
        SATIR KAYDA GİDİYOR — F-NTF-09: "Gidilecek yeri olmayan bildirim üretilmez." Yine de
        `linkUrl` boş gelebilir (eski kayıt) ya da yol bir ekrana çevrilemeyebilir; o zaman düğme
        HİÇ çıkmıyor. Tıklanıp hiçbir yere gitmeyen bir düğme, bozuk bir düğmedir.
      */}
      {bildirim.linkUrl && onGit && bildirimHedefi(bildirim.linkUrl) !== null && (
        <Buton
          gorunum="ikincil"
          onClick={() => {
            onGit(bildirim.linkUrl!);
            if (!bildirim.isRead) onOkundu();
          }}
        >
          Kayda git
        </Buton>
      )}
      {!bildirim.isRead && (
        <Buton gorunum="kesikli" yukleniyor={isleniyor} onClick={onOkundu}>
          Okundu işaretle
        </Buton>
      )}
    </div>
  );
}
